import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@material-tailwind/react";
import { TestableButton, TestableText } from "@components/general/TestableComponents";
import useEvent from "@datahooks/events/useEvent";
import AlertManager from "@components/general/AlertManager";

export default function DeleteEventDialog({
  eventId,
  testLinkId,
  eventLevel,
  isOpen,
  onClose,
  onDelete,
  onDetach,
}) {
  const { event, error, isLoading, refetch } = useEvent(eventId, { enabled: isOpen });

  useEffect(() => {
    if (isOpen && refetch) {
        refetch();
    }
  }, [isOpen, refetch]);

  const linkCount = event?.link_count || 1;

  const isTestLevel = eventLevel === "Test";
  const showDetachOption = isTestLevel && linkCount > 1 && !!testLinkId;

  const renderMessage = () => {
    if ((!isTestLevel) || (linkCount === 1)) {
      return "Are you sure you want to delete this event permanently?";
    } else {
      return `This event is linked to ${linkCount} tests.\n\nDo you want to delete this event from all tests or just from this test?`;
    }
  };

  return (
    <Dialog data-testid="delete-event-dialog" open={isOpen} handler={onClose}>
      <DialogHeader>Delete Event</DialogHeader>
      <DialogBody>
        {isLoading && <div>Loading event info...</div>}
        {error && <AlertManager errorMessage={`Error loading event: ${error.message}`} />}
        {!isLoading && !error && <TestableText dataTestId="delete-event-message">{renderMessage()}</TestableText>}
      </DialogBody>
      <DialogFooter className="flex justify-end gap-2">
        <TestableButton
          dataTestId="delete-event-cancel"
          variant="outlined"
          onClick={onClose}
        >
          Cancel
        </TestableButton>

        {!isLoading && !error && (
          <>
            <TestableButton
              dataTestId="delete-event-confirm"
              color={showDetachOption ? "blue" : "pink"}
              onClick={onDelete}
            >
              Delete {isTestLevel && linkCount > 1 ? "from all tests" : ""}
            </TestableButton>
            {showDetachOption && (
              <TestableButton
                dataTestId="delete-event-detach"
                color="pink"
                onClick={onDetach}
              >
                Delete from this test only
              </TestableButton>
            )}
          </>
        )}
      </DialogFooter>
    </Dialog>
  );
}

DeleteEventDialog.propTypes = {
  eventId: PropTypes.number.isRequired,
  testLinkId: PropTypes.number,
  eventLevel: PropTypes.oneOf(["Project", "Test", "Release"]).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired, // Should performEventDelete in parent
  onDetach: PropTypes.func, // Optional, only used for test events
};
